import { Outlet, NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, ScanLine, Settings, Calendar, LogOut, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import EventSwitcher from '@/components/EventSwitcher';
import { useT, useLanguage, type Lang } from '@/i18n/LanguageContext';

export default function AppLayout() {
  const t = useT();
  const { lang, setLang } = useLanguage();
  const user = useAuthStore((s) => s.user);
  const company = useAuthStore((s) => s.company);
  const logout = useAuthStore((s) => s.logout);

  const NAV_ITEMS = [
    { path: '/', label: t.nav.dashboard, icon: LayoutDashboard },
    { path: '/leads', label: t.nav.leads, icon: Users },
    { path: '/events', label: t.nav.events, icon: Calendar },
    { path: '/settings', label: t.nav.settings, icon: Settings },
  ];

  const handleLogout = () => {
    if (window.confirm(t.common.confirmSignOut)) {
      logout();
    }
  };

  return (
    <div className="min-h-screen bg-reevol-bg flex flex-col md:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-56 lg:w-60 bg-reevol-sidebar border-r border-reevol-border flex-col flex-shrink-0">
        <div className="p-4 border-b border-reevol-border">
          <img src="/reevol-dark.png" alt="Reevol" className="h-14 mb-1" />
          <p className="text-xs text-reevol-text-muted mt-0.5">{t.nav.leadInsight}</p>
        </div>
        <div className="px-2 py-2 border-b border-reevol-border">
          <EventSwitcher />
        </div>
        <nav className="flex-1 py-3">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 mx-2 rounded-xl text-sm transition-all duration-150 ${
                  isActive
                    ? 'bg-reevol-indigo/10 text-reevol-text font-medium border-l-[3px] border-reevol-indigo'
                    : 'text-reevol-text-secondary hover:bg-black/[0.04] hover:text-reevol-text'
                }`
              }
            >
              <item.icon size={18} strokeWidth={1.5} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {user?.isSuperAdmin && (
            <NavLink
              to="/super-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 mx-2 rounded-xl text-sm transition-all duration-150 ${
                  isActive
                    ? 'bg-reevol-indigo/10 text-reevol-text font-medium border-l-[3px] border-reevol-indigo'
                    : 'text-reevol-text-secondary hover:bg-black/[0.04] hover:text-reevol-text'
                }`
              }
            >
              <ShieldCheck size={18} strokeWidth={1.5} />
              <span>Platform Admin</span>
            </NavLink>
          )}
          <div className="px-3 mt-4">
            <NavLink
              to="/scan"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-full bg-[#523EF7] text-white text-sm font-semibold shadow-[0_4px_16px_rgba(82,62,247,0.3)] hover:bg-[#3D2DD4] hover:-translate-y-px hover:shadow-[0_6px_24px_rgba(82,62,247,0.35)] transition-all duration-200"
            >
              <ScanLine size={16} strokeWidth={2} />
              {t.nav.newCheck}
            </NavLink>
          </div>
        </nav>
        <div className="p-4 border-t border-reevol-border space-y-2">
          {/* Language switcher */}
          <div className="flex items-center gap-1 px-1">
            {(['en', 'zh'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`flex-1 text-center px-2 py-1 rounded-lg text-xs transition-colors ${
                  lang === l
                    ? 'bg-reevol-indigo/10 text-reevol-indigo font-medium'
                    : 'text-reevol-text-muted hover:text-reevol-text'
                }`}
              >
                {t.lang[l]}
              </button>
            ))}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-sm text-reevol-text-muted hover:bg-red-500/10 hover:text-red-400 transition-colors"
          >
            <LogOut size={16} strokeWidth={1.5} />
            {t.nav.signOut}
          </button>
          <p className="text-[11px] text-reevol-text-muted">v0.2.0 · Reevol</p>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0 pb-16 md:pb-0">
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-reevol-sidebar border-t border-reevol-border flex z-50 safe-area-bottom">
        {NAV_ITEMS.filter((item) => item.path !== '/events').map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-2 pt-2.5 transition-colors ${
                isActive ? 'text-reevol-cyan' : 'text-reevol-text-muted'
              }`
            }
          >
            <item.icon size={22} strokeWidth={1.5} />
            <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
          </NavLink>
        ))}
        <NavLink
          to="/scan"
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center py-2 pt-2.5 transition-colors ${
              isActive ? 'text-[#523EF7]' : 'text-[#523EF7]/70'
            }`
          }
        >
          <ScanLine size={22} strokeWidth={1.5} />
          <span className="text-[10px] mt-0.5 font-semibold">{t.nav.newCheck}</span>
        </NavLink>
      </nav>
    </div>
  );
}
